<script setup lang="ts">
import type { ImageHtmlSettings, ImageSize, ImageUrls } from "@/services/images";
import { type Image, ImageSize2Height, ImageSize2Width } from "@/services/images";

const Loader = defineAsyncComponent(() => import("@/components/LoadingSmall.vue"));

const FallbackExtension = "jpeg";

defineOptions({
  inheritAttrs: false,
});

const props = defineProps({
  image: {
    type: Object as PropType<Image>,
    required: true,
  },
  settings: {
    type: [String, Object] as PropType<ImageSize | ImageHtmlSettings>,
    required: true,
  },
});

// <img ... />
const fallbackImage = ref();
// <source ... /> array
type Source = {
  extension: string;
  url: string;
  media?: string;
};
const sourceImages = ref<Source[]>();

const attrWidth = ref<number>();
const attrHeight = ref<number>();

// Helpers
const factorySourceImages = (images: ImageUrls, media?: string): Source[] => Object.entries(images).map(([extension, url]) => ({ extension, url, media }));
const mergeToSourceImages = (sources: Source[]) => {
  sourceImages.value = sourceImages.value ?? [];
  sourceImages.value?.push(...sources);
};
const parseSettingsSingleImage = (size: ImageSize): void => {
  const urlsByExtension = props.image.sizes[size];
  if (urlsByExtension) {
    const { [FallbackExtension]: fallbackUrl, ...sourceUrls } = urlsByExtension;
    fallbackImage.value = fallbackUrl;
    attrWidth.value = ImageSize2Width[size];
    attrHeight.value = ImageSize2Height[size];
    mergeToSourceImages(factorySourceImages(sourceUrls));
  } else {
    throw new Error("DEV: Requested non-existing image size.");
  }
};
const parseSettingsMultipleImages = (settings: ImageHtmlSettings): void => {
  settings.sources.forEach((source) => {
    const urlsByExtension = props.image.sizes[source.size];
    if (urlsByExtension) {
      const media = "(" + source.media + ")";
      const sourceImages = factorySourceImages(urlsByExtension, media);
      mergeToSourceImages(sourceImages);
    } else {
      throw new Error("DEV: Requested non-existing image size.");
    }
  });
  parseSettingsSingleImage(settings.fallbackSize);
};

// Setup fallbackImage and sourceImages variables
const parseSettings = () => {
  sourceImages.value = [];
  if (typeof props.settings === "string") {
    parseSettingsSingleImage(props.settings);
  } else {
    parseSettingsMultipleImages(props.settings);
  }
};
watch(() => [props.settings, props.image], parseSettings, { immediate: true });

// State handling
const isError = ref(false);
const isLoading = ref(true);
const handleLoadingError = () => {
  isError.value = true;
  isLoading.value = false;
};
const handleImageLoaded = () => {
  isLoading.value = false;
  isError.value = false;
};
</script>

<template>
  <div class="wrapper">
    <picture>
      <source v-for="source in sourceImages" :key="source.url" :srcset="source.url" :type="'image/' + source.extension" :media="source.media" />
      <img v-if="fallbackImage" :src="fallbackImage" :alt="image.name" :title="image.name" v-bind="$attrs" loading="lazy" :width="attrWidth" :height="attrHeight" class="image" @error="handleLoadingError" @load="handleImageLoaded" />
    </picture>
    <div v-if="isLoading" class="loader-wrapper">
      <Loader color="var(--grayTransparent100)" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.wrapper {
  position: relative;
  width: 100%;
  height: 100%;
}
.image {
  display: block;
  font-size: 10px;
}
.loader-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: var(--gray50);
  z-index: 1;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
